import * as types from '../mutation-types';
import api from '../../api/modules/quizzes';
import { Module } from 'vuex';

interface State {
  quizzes: QuizResponse[] | null;
  quiz: QuizResponse | null;
}

const initialState: State = {
  quizzes: null,
  quiz: null,
};

const quizModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    quizzes: state => state.quizzes,
    quiz: state => state.quiz,
  },
  actions: {
    async refreshQuizzes({ commit }): Promise<QuizResponse[]> {
      const quizzes = await api.getQuizzes();
      commit(types.SET_QUIZZES, quizzes);
      return quizzes;
    },
    async getQuizzesStats(_): Promise<AllQuizzesStatsResponse> {
      const quizzesStats = await api.getQuizzesStats();
      return quizzesStats;
    },
    async getQuiz({ commit }, id): Promise<QuizResponse> {
      const quiz = await api.getQuiz(id);
      commit(types.SET_QUIZ, quiz);
      return quiz;
    },
    async getQuizStats({ commit }, id): Promise<QuizStatsResponse> {
      const quizStats = await api.getQuizStats(id);
      return quizStats;
    },
    async getQuizSubmissions(
      { commit },
      payload
    ): Promise<PaginatedResponse<TeacherSolvedQuizListResponse>> {
      const { page, quizId, q, status, startDate, endDate } = payload;
      const quizSubmissions = await api.getQuizSubmissions(
        page,
        quizId,
        q,
        status,
        startDate,
        endDate
      );
      return quizSubmissions;
    },
    async getQuizSubmission(
      { commit },
      { quizId, submissionId }
    ): Promise<TeacherSolvedQuizResponse> {
      return await api.getQuizSubmission(quizId, submissionId);
    },
    async deleteQuizSubmission(
      { commit },
      { quizId, submissionId }
    ): Promise<any> {
      return await api.deleteQuizSubmission(quizId, submissionId);
    },
    async finishGradingQuizSubmission(
      { commit },
      { quizId, submissionId }
    ): Promise<TeacherSolvedQuizResponse> {
      return await api.finishGradingQuizSubmission(quizId, submissionId);
    },
    async autoGradeQuizSubmission(
      { commit },
      { quizId, submissionId }
    ): Promise<TeacherSolvedQuizResponse> {
      return await api.autoGradeQuizSubmission(quizId, submissionId);
    },
    async markSolvedAnswerCorrect(
      { commit },
      { quizId, submissionId, answerId }
    ): Promise<TeacherSolvedQuitAnswerGradingResponse> {
      return await api.markSolvedAnswerCorrect(quizId, submissionId, answerId);
    },
    async markSolvedAnswerIncorrect(
      { commit },
      { quizId, submissionId, answerId }
    ): Promise<TeacherSolvedQuitAnswerGradingResponse> {
      return await api.markSolvedAnswerIncorrect(
        quizId,
        submissionId,
        answerId
      );
    },
    async createQuiz({ commit }): Promise<QuizResponse> {
      const quiz = await api.createQuiz();
      commit(types.SET_QUIZ, quiz);
      return quiz;
    },
    async updateQuiz({ commit }, { id, data }): Promise<QuizResponse> {
      const quiz = await api.updateQuiz(id, data);
      commit(types.SET_QUIZ, quiz);
      return quiz;
    },
    async deleteQuiz({ commit }, id: number) {
      await api.deleteQuiz(id);
      commit(types.SET_QUIZ, null);
    },
    async deleteTeacherQuizSubmission({ commit }, id: number) {
      await api.deleteTeacherQuizSubmission(id);
    },
    async createQuizQuestion(
      _,
      { quizId, data }
    ): Promise<QuizQuestionResponse> {
      const quizQuestion = await api.createQuizQuestion(quizId, data);
      return quizQuestion;
    },
    async updateQuizQuestion(
      _,
      { quizId, data }
    ): Promise<QuizQuestionResponse> {
      const quizQuestion = await api.updateQuizQuestion(quizId, data);
      return quizQuestion;
    },
    async deleteQuizQuestion(_, { quizId, questionId }) {
      await api.deleteQuizQuestion(quizId, questionId);
    },
    async duplicateQuizQuestion(_, { quizId, questionId }) {
      return await api.duplicateQuizQuestion(quizId, questionId);
    },
    async reorderQuizQuestions({ commit }, { quizId, questions }) {
      commit(types.SET_QUIZ_QUESTIONS, [...questions]);
      const questionsPosition: QuizQuestionReorder[] = questions.map(
        ({ id }, position) => ({
          question_id: id,
          position,
        })
      );

      await api.reorderQuizQuestions(quizId, questionsPosition);
    },
    addQuizQuestion({ commit, state }, question: QuizQuestionResponse) {
      const questions = [...(state.quiz?.quiz_questions || [])];
      questions.push(question);
      commit(types.SET_QUIZ_QUESTIONS, questions);
    },
    refreshQuizQuestion({ commit, state }, question: QuizQuestionResponse) {
      if (!state.quiz?.quiz_questions) return;

      const questionIndex = state.quiz.quiz_questions.findIndex(
        ({ id }) => question.id === id
      );

      const quizQuestions = [...state.quiz.quiz_questions];
      quizQuestions.splice(questionIndex, 1, question);

      commit(types.SET_QUIZ_QUESTIONS, quizQuestions);
    },
    removeQuizQuestion({ commit, state }, id) {
      let questions = state.quiz?.quiz_questions || [];
      questions = questions.filter(question => question.id !== id);
      commit(types.SET_QUIZ_QUESTIONS, questions);
    },
    async createQuizAnswer(
      _,
      { quizId, questioId, data }
    ): Promise<QuizAnswerResponse> {
      const quizAnswer = await api.createQuizAnswer(quizId, questioId, data);
      return quizAnswer;
    },
    async updateQuizAnswer(
      _,
      { quizId, questioId, data }
    ): Promise<QuizAnswerResponse> {
      const quizAnswer = await api.updateQuizAnswer(quizId, questioId, data);
      return quizAnswer;
    },
    async deleteQuizAnswer(_, { quizId, questioId, data }) {
      await api.deleteQuizAnswer(quizId, questioId, data);
    },
    addQuizAnswer({ commit, state }, { questioId, answer }) {
      const questionIndex = state.quiz?.quiz_questions.findIndex(
        ({ id }) => id === questioId
      );

      if (!questionIndex) return;
      const answers = state.quiz?.quiz_questions?.[questionIndex]
        .answers as QuizAnswerResponse[];

      commit(types.SET_QUIZ_ANSWERS, {
        questionIndex,
        answers: [...answers, answer],
      });
    },
    removeQuizAnswer({ commit, state }, id) {
      let questions = state.quiz?.quiz_questions || [];
      questions = questions.filter(question => question.id === id);
      commit(types.SET_QUIZ_ANSWERS, questions);
    },
  },
  mutations: {
    [types.SET_QUIZZES](state: State, quizzes: QuizResponse[]) {
      state.quizzes = quizzes;
    },
    [types.SET_QUIZ](state: State, quiz: QuizResponse) {
      state.quiz = quiz;
    },
    [types.SET_QUIZ_QUESTIONS](
      state: State,
      questions: QuizQuestionResponse[]
    ) {
      if (state.quiz) {
        state.quiz.quiz_questions = questions;
      }
    },
    [types.SET_QUIZ_ANSWERS](state: State, { questionIndex, answers }) {
      if (state.quiz?.quiz_questions) {
        state.quiz.quiz_questions[questionIndex].answers = answers;
      }
    },
  },
};

export default quizModule;
